import PublicationDetails from '../Publications/PublicationDetails';
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import "primeicons/primeicons.css";
import "../../styles/Map.css";
import { drawCircleOnMap } from "./utils/mapUtils.js";
import { pathToPolygon, polygonToPath } from "./utils/drawingUtils.js";

import PriceMarkers from "./PriceMarkers";
import { formatPropertyPrice, formatPublisherName, pricePerSquareMeter } from "./propertyMap";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = { lat: -34.6037, lng: -58.3816 };
const LIBRARIES = ["places", "drawing", "geometry"];

/**
 * Map del Home — lectura/visualización de resultados.
 *
 * IMPORTANTE: en el Home NO se permite dibujar ni limpiar zonas. La
 * delimitación (radio o polígonos) se definió en el wizard y se persiste
 * en la sesión. El toggle Radio/Zonas vive en el Navbar.
 *
 * Props:
 *  - searchMode: 'radius' | 'polygons'
 *  - center, radius: usados en radius
 *  - polygonsPaths: Array<Array<{lat,lng}>> persistidos
 *  - selectedPlace: ubicación del cliente (se muestra SIEMPRE como pin rojo)
 */
export default function Map({
  displayedProperties = [],
  center,
  onMarkerClick,
  selectedProperty,
  visitedProperties = [],
  selectedPlace,
  radius = 1000,
  searchMode = "radius",
  polygonsPaths = [],
  originalPolygonsPaths = [],
  listOpen = true,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [map, setMap] = useState(null);
  const [failedImage, setFailedImage] = useState(null);
  const markerProperties = useMemo(() => displayedProperties.map((property) => ({
    ...property,
    lat: Number(property.lat ?? property.latitude ?? property.latitude_value),
    lng: Number(property.lng ?? property.longitude ?? property.longitude_value),
  })).filter((property) => Number.isFinite(property.lat) && Number.isFinite(property.lng)
    && Math.abs(property.lat) <= 90 && Math.abs(property.lng) <= 180), [displayedProperties]);
  const containerRef = useRef(null);
  const circleRef = useRef(null);
  const polygonsRef = useRef([]); // instancias google.maps.Polygon

  const clearPolygonInstances = () => {
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];
  };

  // --- Sincronizar instancias de polígonos con polygonsPaths (solo render)
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (searchMode !== "polygons") {
      clearPolygonInstances();
      return;
    }

    const currentPaths = polygonsRef.current.map(polygonToPath);
    const same =
      currentPaths.length === polygonsPaths.length &&
      JSON.stringify(currentPaths) === JSON.stringify(polygonsPaths);
    if (same) return;

    clearPolygonInstances();
    polygonsRef.current = polygonsPaths
      .map((path) => pathToPolygon(path, map))
      .filter(Boolean);
  }, [isLoaded, map, searchMode, polygonsPaths]);

  // --- Círculo de radio (solo en modo radius)
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (searchMode !== "radius" || !center) return;

    const centerLatLng = new window.google.maps.LatLng(center.lat, center.lng);
    circleRef.current = drawCircleOnMap(map, centerLatLng, radius, {
      strokeColor: "#1976D2",
      fillColor: "#1976D2",
      fillOpacity: 0.12,
    });
  }, [isLoaded, map, searchMode, center, radius]);

  const fitSearchArea = useCallback(() => {
    if (!isLoaded || !map) return;
    const padding = (() => {
      const panel = containerRef.current?.closest(".home-workspace")?.querySelector(".home-properties");
      if (!listOpen || !panel) return 36;
      if (window.innerWidth <= 800) {
        return { top: 24, right: 24, bottom: panel.clientHeight + 16, left: 24 };
      }
      return { top: 36, right: panel.clientWidth + 16, bottom: 36, left: 36 };
    })();
    if (searchMode === "polygons" && polygonsPaths.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      polygonsPaths.forEach((path) =>
        path.forEach((pt) => bounds.extend({ lat: Number(pt.lat), lng: Number(pt.lng) }))
      );
      if (!bounds.isEmpty()) map.fitBounds(bounds, padding);
    } else if (searchMode === "radius" && center) {
      const area = new window.google.maps.Circle({
        center: { lat: Number(center.lat), lng: Number(center.lng) },
        radius: Number(radius),
      });
      const bounds = area.getBounds();
      if (bounds) map.fitBounds(bounds, padding);
    }
  }, [isLoaded, map, searchMode, polygonsPaths, center, radius, listOpen]);

  useEffect(() => { fitSearchArea(); }, [fitSearchArea]);

  // The map occupies its real grid column. Refit when the navbar wraps or the
  // viewport changes, so the search area stays visible beside the results.
  useEffect(() => {
    const container = containerRef.current;
    if (!map || !container) return;
    let frame;
    let settle;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
      frame = requestAnimationFrame(() => {
        window.google.maps.event.trigger(map, "resize");
      });
      settle = setTimeout(fitSearchArea, 180);
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
      observer.disconnect();
    };
  }, [map, fitSearchArea]);

  useEffect(() => {
    if (!isLoaded || !map || searchMode !== "polygons") return;
    const originals = originalPolygonsPaths.map((path) => pathToPolygon(path, map, {
      fillOpacity: 0,
      strokeColor: "#a8a29e",
      strokeWeight: 1,
      strokeOpacity: 0.8,
      clickable: false,
    })).filter(Boolean);
    return () => originals.forEach((polygon) => polygon.setMap(null));
  }, [isLoaded, map, searchMode, originalPolygonsPaths]);

  useEffect(() => () => {
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    circleRef.current?.setMap(null);
  }, []);

  // Center a selected property in the part of the map left visible by the list.
  // Wait for its slide transition and the search-area refit to finish first.
  useEffect(() => {
    if (!map || !selectedProperty) return;
    const lat = Number(selectedProperty.lat ?? selectedProperty.latitude);
    const lng = Number(selectedProperty.lng ?? selectedProperty.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const panel = container.closest(".home-workspace")?.querySelector(".home-properties");
      const mobile = window.innerWidth <= 800;
      const coveredWidth = listOpen && !mobile ? panel?.clientWidth || 0 : 0;
      const coveredHeight = listOpen && mobile ? panel?.clientHeight || 0 : 0;
      const visibleHeight = container.clientHeight - coveredHeight;
      const anchorY = Math.max(visibleHeight / 2, Math.min(380, visibleHeight - 24));
      map.panTo({ lat, lng });
      map.panBy(coveredWidth / 2, container.clientHeight / 2 - anchorY);
    }, 460);
    return () => clearTimeout(timer);
  }, [map, selectedProperty, listOpen]);

  if (loadError) return <div>Error al cargar el mapa</div>;
  if (!isLoaded) return <div>Cargando mapa...</div>;

  const selectedLat = selectedProperty
    ? Number(selectedProperty.lat ?? selectedProperty.latitude ?? NaN)
    : null;
  const selectedLng = selectedProperty
    ? Number(selectedProperty.lng ?? selectedProperty.longitude ?? NaN)
    : null;
  const hasSelectedPos = selectedProperty && Number.isFinite(selectedLat) && Number.isFinite(selectedLng);

  const selectedPpsqm = pricePerSquareMeter(selectedProperty?.price, selectedProperty?.square_meters_covered);
  const publisherName = formatPublisherName(selectedProperty?.owner_type);

  return (
    <div className="map-container" ref={containerRef}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center || defaultCenter}
        zoom={12}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          gestureHandling: "greedy",
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.LEFT_CENTER,
          },
        }}
        onLoad={(mapInstance) => setMap(mapInstance)}
      >
        {/* Ubicación de la propiedad del cliente (pin rojo default de Google) */}
        {selectedPlace?.lat && selectedPlace?.lng && (
          <Marker
            position={{
              lat: Number(selectedPlace.lat),
              lng: Number(selectedPlace.lng),
            }}
            title={selectedPlace.formatted_address || "Ubicación de tu propiedad"}
            zIndex={9999}
          />
        )}

        <PriceMarkers properties={markerProperties} selectedId={selectedProperty?.id}
          visitedIds={visitedProperties} onSelect={onMarkerClick} />

        {/* Mini-tarjeta al clickear un pin */}
        {selectedProperty && hasSelectedPos && (
          <InfoWindow
            position={{ lat: selectedLat, lng: selectedLng }}
            onCloseClick={() => onMarkerClick?.(null)}
            options={{
              pixelOffset: new window.google.maps.Size(0, -22),
              maxWidth: 340,
            }}
          >
            <div className="map-info-card">
              <div className="map-info-media">
                <button
                  type="button"
                  className="map-info-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkerClick?.(null);
                  }}
                  aria-label="Cerrar"
                >
                  <i className="pi pi-times" />
                </button>
                {selectedProperty.image_url && failedImage !== selectedProperty.image_url ? (
                  <img
                    className="map-info-thumb"
                    src={selectedProperty.image_url}
                    alt={`Propiedad en ${selectedProperty.address || selectedProperty.location || "la zona"}`}
                    onError={() => setFailedImage(selectedProperty.image_url)}
                  />
                ) : (
                  <div className="map-info-thumb map-info-thumb--empty" aria-hidden="true">
                    <i className="pi pi-image" />
                  </div>
                )}
              </div>
              <div className="map-info-body">
                <div className="map-info-address" title={selectedProperty.address}>
                  {selectedProperty.address || "Sin dirección"}
                </div>
                <div className="map-info-price">{formatPropertyPrice(selectedProperty.price, selectedProperty.price_currency)}</div>
                {selectedPpsqm != null && <div className="map-info-ppsqm">
                  {formatPropertyPrice(selectedPpsqm, selectedProperty.price_currency)} / m²
                  <span>cubierto</span>
                </div>}
                <PublicationDetails property={selectedProperty} compact />
                {publisherName && (
                  <div className="map-info-publisher">
                    <i className="pi pi-building" aria-hidden="true" />
                    Publica: <span className="map-info-publisher__name">{publisherName}</span>
                  </div>
                )}
                {(selectedProperty.rooms != null ||
                  selectedProperty.bedrooms != null ||
                  selectedProperty.bathrooms != null ||
                  selectedProperty.square_meters_covered != null) && (
                  <div className="map-info-chips">
                    {selectedProperty.rooms != null && (
                      <span className="map-info-chip">
                        <i className="pi pi-th-large" />
                        {selectedProperty.rooms} amb
                      </span>
                    )}
                    {selectedProperty.bedrooms != null && (
                      <span className="map-info-chip">
                        <i className="pi pi-moon" />
                        {selectedProperty.bedrooms} dorm
                      </span>
                    )}
                    {selectedProperty.bathrooms != null && (
                      <span className="map-info-chip">
                        <i className="pi pi-bookmark" />
                        {selectedProperty.bathrooms} baños
                      </span>
                    )}
                    {selectedProperty.square_meters_covered != null && (
                      <span className="map-info-chip">
                        <i className="pi pi-home" />
                        {selectedProperty.square_meters_covered} m²
                      </span>
                    )}
                  </div>
                )}
                {selectedProperty.url && (
                  <a
                    className="map-info-cta"
                    href={selectedProperty.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ver publicación
                    <i className="pi pi-external-link" />
                  </a>
                )}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}
